import { useId, useRef, useState } from "react";
import {
  formatBytes,
  formatWeekdayDateOrDash,
} from "@shared-core/domain/formatters";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import type {
  ArtworkWrites,
  SlotView,
  VersionView,
} from "@shared-core/data/customer-link-adapter";
import {
  assignmentOf,
  proposedTags,
} from "@shared-core/domain/combined-file-tagging";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { cn } from "../../app/components/primitives/utils";
import { SupplyGuide } from "./SupplyGuide";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { ArtworkCheckIssueGroups } from "./ArtworkCheckIssueGroups";
import { MAX_ARTWORK_FILE_SIZE_BYTES, validateArtworkPdf } from "./artworkFile";
import { ATTENTION_TINT } from "./attention";
import { PageTagging } from "./PageTagging";
import type { FetchFile } from "./useFetchedFile";

// One version's files on a quote or order line: a slot per part (one slot
// for a one-part item), each with the file we hold and its check result in
// the customer tiers (doc 39 §6), and the way to send a file or a new one.
// An unnamed version asks for its name first (doc 40 §10: labels can change
// only until that version's artwork is uploaded). Upload buttons are
// outlined (Ben, 5 Oct). Every write goes to the URL the read handed out
// (doc 66 §6.5): no URL, no button.

// The result is said once (Ben, 5 Oct): one box per tier, always shown. A
// file held for our team, or not yet checked, shows only its neutral
// sentence: no findings, no dot (doc 66 §6.3).
function SlotResult({ slot }: { slot: SlotView }) {
  if (!slot.file || !slot.verdict) return null;
  if (slot.verdict === "passed") {
    return (
      <p className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800 dark:border-green-900 dark:bg-green-950/20 dark:text-green-300">
        <CheckCircle2 className="size-4 shrink-0" />
        {slot.verdict_sentence}
      </p>
    );
  }
  if (slot.verdict === "held" || slot.verdict === "checking") {
    return (
      <p className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
        <Clock className="size-4 shrink-0 text-muted-foreground" />
        {slot.verdict_sentence}
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <ArtworkCheckIssueGroups findings={slot.findings} />
    </div>
  );
}

function FileChip({ slot }: { slot: SlotView }) {
  if (!slot.file) return null;
  const f = slot.file;
  return (
    <div className="flex min-w-0 items-center gap-2 text-sm">
      <FileText className="size-4 shrink-0 text-muted-foreground" />
      <span className="truncate font-medium">{f.file_name}</span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {f.reused_from_revision
          ? "same as before"
          : `${formatWeekdayDateOrDash(f.uploaded_at)} · ${f.uploaded_by}`}
      </span>
      {f.split_from && (
        <span className="shrink-0 text-xs text-muted-foreground">
          From {f.split_from.file_name}, {f.split_from.pages_label}
        </span>
      )}
    </div>
  );
}

function Uploader({
  label,
  onUpload,
  onCancel,
}: {
  label: string;
  onUpload: (file: File) => Promise<void>;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function send(file: File | null) {
    setSubmitError(null);
    if (!file) return;
    const error = validateArtworkPdf(file);
    setFileError(error);
    if (error) return;
    setSubmitting(true);
    try {
      await onUpload(file);
    } catch (err) {
      setSubmitting(false);
      setSubmitError(
        mutationErrorMessage(err, "We couldn't take your file just now."),
      );
    }
  }

  return (
    <div className="space-y-3 rounded-md border border-dashed border-input p-3">
      {submitting ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Checking your file…
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            aria-label={label}
            onChange={(e) => void send(e.target.files?.[0] ?? null)}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="size-4" />
            Choose a PDF
          </Button>
          <span className="text-xs text-muted-foreground">
            PDF only, up to {formatBytes(MAX_ARTWORK_FILE_SIZE_BYTES)}. We check
            it straight away.
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="ml-auto"
            aria-label="Cancel upload"
            onClick={onCancel}
          >
            <X className="size-4" />
          </Button>
        </div>
      )}
      {fileError && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {fileError}
        </p>
      )}
      {submitError && (
        <p
          role="alert"
          className="flex gap-2 text-sm text-amber-800 dark:text-amber-300"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>
            {submitError} Try again in a few minutes, or get in touch and
            we&apos;ll help.
          </span>
        </p>
      )}
    </div>
  );
}

// One file for every part (doc 87 §6): above the slots, the offer to send it; once it is in, the read of it for the
// customer to confirm (agreed: one sentence with Looks right and Change; unsure: the page tagging opens directly).
function PendingFile({
  pending,
  writes,
  onChanged,
  fetchFile,
}: {
  pending: NonNullable<VersionView["pending_combined_file"]>;
  writes: ArtworkWrites;
  onChanged: () => Promise<void>;
  fetchFile: FetchFile | null;
}) {
  const isAgreed = pending.outcome === "agreed";
  const [isChanging, setIsChanging] = useState(!isAgreed);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(step: () => Promise<void>, failure: string) {
    setError(null);
    setBusy(true);
    try {
      await step();
      await onChanged();
    } catch (err) {
      setBusy(false);
      setError(mutationErrorMessage(err, failure));
    }
  }
  const submitTags = (request: ReturnType<typeof assignmentOf>) =>
    run(
      () => writes.splitCombinedFile(pending.split_url, request),
      "We couldn't split your file just now.",
    );

  if (!isChanging) {
    return (
      <div className={cn("space-y-2 rounded-md p-3", ATTENTION_TINT)}>
        <p className="text-sm">{pending.summary}</p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() =>
              void submitTags(assignmentOf(pending, proposedTags(pending)))
            }
          >
            Looks right
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => setIsChanging(true)}
          >
            Change
          </Button>
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-700 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className={cn("rounded-md p-3", ATTENTION_TINT)}>
      <PageTagging
        pending={pending}
        fetchFile={fetchFile}
        busy={busy}
        error={error}
        // A file we read confidently goes back to its sentence; one we were unsure of has nothing to go back to.
        cancelLabel={isAgreed ? "Cancel" : "Send a different file"}
        onConfirm={(request) => void submitTags(request)}
        onCancel={() => {
          if (isAgreed) {
            setError(null);
            setIsChanging(false);
          } else {
            void run(
              () => writes.discardCombinedFile(pending.discard_url),
              "We couldn't cancel that just now.",
            );
          }
        }}
      />
    </div>
  );
}

function WholeFileSection({
  version,
  writes,
  onChanged,
  fetchFile,
}: {
  version: VersionView;
  writes: ArtworkWrites;
  onChanged: () => Promise<void>;
  fetchFile: FetchFile | null;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const pending = version.pending_combined_file ?? null;
  if (pending) {
    return (
      <PendingFile
        key={pending.id}
        pending={pending}
        writes={writes}
        onChanged={onChanged}
        fetchFile={fetchFile}
      />
    );
  }
  const uploadUrl = version.combined_upload_url ?? null;
  if (!uploadUrl) return null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-input p-3">
        <p className="text-sm">
          Got one file with everything in it? Upload it here and we&apos;ll
          split it.
        </p>
        {!isUploading && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setIsUploading(true)}
          >
            <Upload className="size-4" />
            Upload one file
          </Button>
        )}
      </div>
      {isUploading && (
        <Uploader
          label="Choose the file with everything in it"
          onCancel={() => setIsUploading(false)}
          onUpload={async (f) => {
            await writes.uploadWholeFile(uploadUrl, f);
            setIsUploading(false);
            await onChanged();
          }}
        />
      )}
    </div>
  );
}

function NameFirst({
  label,
  onName,
}: {
  label: string;
  onName: (name: string) => Promise<void>;
}) {
  const id = useId();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  return (
    <form
      className={cn("space-y-2 rounded-md p-3", ATTENTION_TINT)}
      onSubmit={async (e) => {
        e.preventDefault();
        if (name.trim() === "") {
          setError(
            `Give version ${label} a name so we know which file is which.`,
          );
          return;
        }
        setSaving(true);
        try {
          await onName(name);
        } catch (err) {
          setSaving(false);
          setError(mutationErrorMessage(err, "We couldn't save that name."));
        }
      }}
    >
      <label htmlFor={id} className="text-sm font-medium">
        What should we call version {label}?
      </label>
      <p className="text-xs text-muted-foreground">
        Name it before you send its file, for example the site or event it is
        for.
      </p>
      <div className="flex gap-2">
        <Input
          id={id}
          value={name}
          maxLength={60}
          placeholder="e.g. Breakout"
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError(null);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className="max-w-56"
        />
        <Button type="submit" variant="outline" disabled={saving}>
          Save name
        </Button>
      </div>
      {error && (
        <p
          id={`${id}-error`}
          role="alert"
          className="text-sm text-red-700 dark:text-red-400"
        >
          {error}
        </p>
      )}
    </form>
  );
}

export function ArtworkUploadSlots({
  version,
  writes,
  onChanged,
  idleHint,
  isFileOptional = false,
  fetchFile = null,
}: {
  version: VersionView;
  writes: ArtworkWrites;
  /** After a write: re-read the page. */
  onChanged: () => Promise<void>;
  /** Under an empty slot: what happens once the file is in. */
  idleHint: string;
  /** A quote: files are optional before acceptance (Ben, 5 Oct), so an
   *  empty slot is not something owed. */
  isFileOptional?: boolean;
  /** Where a page picture of a file sent for every part is fetched from; null where the page cannot act. */
  fetchFile?: FetchFile | null;
}) {
  const [uploadingPart, setUploadingPart] = useState<string | null | undefined>(
    undefined,
  );
  const [removing, setRemoving] = useState<string | null | undefined>(
    undefined,
  );
  const [isShowingParts, setIsShowingParts] = useState(false);

  if (version.needs_name && version.name_url) {
    const nameUrl = version.name_url;
    return (
      <NameFirst
        label={version.label ?? ""}
        onName={async (name) => {
          await writes.nameVersion(nameUrl, name);
          await onChanged();
        }}
      />
    );
  }

  const isWaitingForTags = version.pending_combined_file != null;
  // The dictionary decides (doc 87 §18): on a one_file item that has the one-file step to show, that step is the ask and
  // each part's own slot waits behind "or send each part separately". A part that already has a file keeps them open, so
  // nothing filed is hidden. A per-part item, and a one_file item with no such step (one part, held, read-only), shows
  // its slots as they are.
  const leadsWithOneFile =
    version.grouping === "one_file" &&
    (version.combined_upload_url != null || isWaitingForTags);
  const showsSlots =
    !leadsWithOneFile ||
    isShowingParts ||
    version.slots.some((slot) => slot.file != null);
  return (
    <div className="space-y-3">
      <WholeFileSection
        version={version}
        writes={writes}
        onChanged={onChanged}
        fetchFile={fetchFile}
      />
      {!showsSlots && (
        <button
          type="button"
          className="text-sm font-medium text-primary underline underline-offset-4 hover:no-underline"
          aria-expanded={false}
          onClick={() => setIsShowingParts(true)}
        >
          or send each part separately
        </button>
      )}
      {showsSlots && (
        <ul className="space-y-3">
          {version.slots.map((slot) => {
            const key = slot.part_key ?? "file";
            const isUploading = uploadingPart === slot.part_key;
            const what = slot.part ? `${slot.part} file` : "file";
            const uploadUrl = slot.upload_url;
            const removeUrl = slot.remove_url;
            return (
              <li
                key={key}
                className={cn(
                  "space-y-2",
                  ((!slot.file && !isFileOptional) ||
                    slot.verdict === "cant_print") &&
                    uploadUrl &&
                    cn("rounded-md p-3", ATTENTION_TINT),
                )}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0 space-y-0.5">
                    {slot.part && (
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {slot.part}
                      </p>
                    )}
                    {slot.file ? (
                      <FileChip slot={slot} />
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {isWaitingForTags
                          ? "Waiting for the page tags"
                          : isFileOptional
                            ? `No ${what} yet. ${idleHint}`
                            : `Waiting for your ${what}. ${idleHint}`}
                      </p>
                    )}
                  </div>
                  {!isUploading && (uploadUrl || removeUrl) && (
                    <div className="flex flex-wrap gap-2">
                      {slot.file && removeUrl && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={removing === slot.part_key}
                          onClick={async () => {
                            setRemoving(slot.part_key);
                            try {
                              await writes.removeFile(removeUrl);
                              await onChanged();
                            } finally {
                              setRemoving(undefined);
                            }
                          }}
                        >
                          <Trash2 className="size-4" />
                          Remove file
                        </Button>
                      )}
                      {uploadUrl && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setUploadingPart(slot.part_key)}
                        >
                          <Upload className="size-4" />
                          {!slot.file
                            ? `Upload ${slot.part ? `the ${slot.part.toLowerCase()} file` : "your file"}`
                            : "Upload a new file"}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {uploadUrl && (
                  <SupplyGuide
                    sentence={slot.supply_sentence}
                    notes={slot.supply_notes}
                    terms={slot.supply_terms}
                  />
                )}
                <SlotResult key={slot.file?.file_name ?? "none"} slot={slot} />
                {isUploading && uploadUrl && (
                  <Uploader
                    label={`Choose the ${what} to upload`}
                    onCancel={() => setUploadingPart(undefined)}
                    onUpload={async (f) => {
                      await writes.uploadFile(uploadUrl, f);
                      setUploadingPart(undefined);
                      await onChanged();
                    }}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
